import React, { useRef, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { BookOpen, Trophy, Sparkles, Flame, CheckCircle2 } from 'lucide-react';

interface FloatingElementProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  xRange?: [number, number];
  yRange?: [number, number];
  rRange?: [number, number];
  duration?: number;
  delay?: number;
}

export const FloatingElement = ({
  children,
  className = '',
  style = {},
  xRange = [-10, 10],
  yRange = [-15, 15],
  rRange = [-4, 4],
  duration = 6,
  delay = 0
}: FloatingElementProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  // Mouse positions relative to the element center
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // Translate mouse positions to degrees of rotation for 3D tilt
  const tiltX = useSpring(useTransform(mouseY, [-120, 120], [15, -15]), { damping: 25, stiffness: 150 });
  const tiltY = useSpring(useTransform(mouseX, [-120, 120], [-15, 15]), { damping: 25, stiffness: 150 });
  const scale = useSpring(isHovered ? 1.06 : 1, { damping: 25, stiffness: 150 });

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    
    // Mouse coords relative to element center
    const clientX = e.clientX - rect.left - width / 2;
    const clientY = e.clientY - rect.top - height / 2;

    mouseX.set(clientX);
    mouseY.set(clientY);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    mouseX.set(0);
    mouseY.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseEnter={() => setIsHovered(true)}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        ...style,
        perspective: 1000,
        rotateX: isHovered ? tiltX : undefined,
        rotateY: isHovered ? tiltY : undefined,
        scale: scale,
        transformStyle: 'preserve-3d',
      }}
      animate={isHovered ? {} : {
        x: [0, xRange[1], xRange[0], 0],
        y: [0, yRange[0], yRange[1], 0],
        rotate: [0, rRange[1], rRange[0], 0],
      }}
      transition={isHovered ? { type: 'spring', stiffness: 200, damping: 25 } : {
        duration: duration,
        repeat: Infinity,
        repeatType: 'loop',
        ease: 'easeInOut',
        delay: delay
      }}
      className={`absolute cursor-pointer select-none ${className}`}
    >
      <div style={{ transform: 'translateZ(30px)' }} className="w-full h-full">
        {children}
      </div>
    </motion.div>
  );
};

export default function AntiGravityHero() {
  return (
    <div className="relative w-full h-[500px] md:h-[600px] max-w-[500px] mx-auto lg:mx-0 flex items-center justify-center">
      {/* Background glowing orb orbits */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,203,169,0.08),transparent_65%)] pointer-events-none" />
      <div className="absolute w-[350px] h-[350px] border border-white/5 rounded-full animate-[spin_60s_linear_infinite] pointer-events-none flex items-center justify-center">
        <div className="w-2 h-2 rounded-full bg-emerald-500/20 absolute -top-1" />
        <div className="w-1.5 h-1.5 rounded-full bg-blue-500/20 absolute -bottom-1" />
      </div>

      <div className="absolute w-[240px] h-[240px] border border-white/5 border-dashed rounded-full animate-[spin_40s_linear_infinite_reverse] pointer-events-none" />

      {/* Element 1: Glassmorphism Course Card (Top Left) */}
      <FloatingElement
        className="top-8 left-0 w-64 z-20"
        xRange={[-12, 8]}
        yRange={[-8, 12]}
        rRange={[-3, 2]}
        duration={7}
        delay={0}
      >
        <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-3xl p-5 shadow-[0_8px_32px_0_rgba(0,0,0,0.3)] text-left">
          <div className="flex justify-between items-center mb-3">
            <span className="px-2.5 py-1 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-full text-[9px] font-bold tracking-wide uppercase">
              উচ্চতর গণিত
            </span>
            <div className="flex -space-x-1.5">
              <span className="w-5 h-5 rounded-full bg-blue-500 border border-black text-[7px] flex items-center justify-center font-bold">A</span>
              <span className="w-5 h-5 rounded-full bg-purple-500 border border-black text-[7px] flex items-center justify-center font-bold">B</span>
            </div>
          </div>
          <h4 className="font-bangla font-extrabold text-sm text-white leading-tight mb-2">
            ত্রিকোণমিতি অধ্যায় ৪: অনুশীলন
          </h4>
          <p className="text-[10px] text-gray-400 mb-4 font-bangla">৯/১০ টি কুইজ সফলভাবে সম্পন্ন</p>
          
          <div className="space-y-1">
            <div className="flex justify-between items-center text-[9px] text-gray-400 font-semibold font-english">
              <span>অগ্রগতি</span>
              <span className="text-emerald-400">৯০%</span>
            </div>
            <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
              <motion.div 
                initial={{ width: 0 }}
                animate={{ width: '90%' }}
                transition={{ duration: 1.5, ease: 'easeOut' }}
                className="h-full bg-gradient-to-r from-emerald-400 to-[#00CBA9] rounded-full" 
              />
            </div>
          </div>
        </div>
      </FloatingElement>

      {/* Element 2: 3D-looking Textbook (Bottom Left) */}
      <FloatingElement
        className="bottom-12 left-4 w-48 z-10"
        xRange={[10, -10]}
        yRange={[12, -12]}
        rRange={[4, -4]}
        duration={8}
        delay={1.5}
      >
        <div className="relative group overflow-hidden bg-gradient-to-br from-indigo-600 to-purple-800 rounded-2xl p-4 shadow-[0_15px_35px_rgba(0,0,0,0.4)] border border-white/10 text-left h-56 flex flex-col justify-between">
          <div className="absolute top-0 right-0 w-32 h-32 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.12),transparent_60%)] pointer-events-none" />
          <div className="flex items-center gap-2 text-white/80">
            <BookOpen className="w-4 h-4 text-purple-300" />
            <span className="text-[8px] uppercase tracking-wider font-extrabold text-purple-200">চর্চাঘর গাইড</span>
          </div>

          <div className="space-y-1 mt-6">
            <h4 className="font-bangla font-black text-base text-white leading-tight">
              পদার্থবিজ্ঞান
            </h4>
            <p className="text-[9px] text-purple-200/80 font-bangla">এইচএসসি ১ম পত্র</p>
          </div>

          <div className="pt-3 border-t border-white/10 flex items-center justify-between">
            <span className="text-[8px] font-english text-purple-300">ভলিউম ০২</span>
            <span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center border border-white/10">
              <Sparkles className="w-2.5 h-2.5 text-[#00CBA9]" />
            </span>
          </div>
        </div>
      </FloatingElement>

      {/* Element 3: Glassmorphism Leaderboard/Stats Card (Right Side) */}
      <FloatingElement
        className="top-24 right-0 w-64 z-20"
        xRange={[-8, 12]}
        yRange={[10, -10]}
        rRange={[-2, 3]}
        duration={6.5}
        delay={3}
      >
        <div className="backdrop-blur-lg bg-black/40 border border-white/10 rounded-3xl p-5 shadow-[0_12px_40px_rgba(0,0,0,0.5)] text-left">
          <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-white/5">
            <div className="w-7 h-7 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400">
              <Trophy className="w-4 h-4" />
            </div>
            <div>
              <h5 className="font-bangla font-extrabold text-xs text-white">সেরা শিক্ষার্থী লিডারবোর্ড</h5>
              <p className="text-[8px] text-gray-500 font-bangla">আজকের শীর্ষ চর্চাকারী</p>
            </div>
          </div>

          <div className="space-y-2.5">
            <div className="flex justify-between items-center p-2 rounded-xl bg-white/5 border border-white/5">
              <div className="flex items-center gap-2">
                <span className="text-[9px] font-extrabold text-amber-400">১</span>
                <div className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold text-[8px]">SA</div>
                <span className="font-bangla text-[9px] text-white">সাকিব আল হাসান</span>
              </div>
              <span className="text-[8px] font-english text-emerald-400 font-bold">+১৫০ XP</span>
            </div>

            <div className="flex justify-between items-center p-2 rounded-xl bg-white/5 border border-white/5">
              <div className="flex items-center gap-2">
                <span className="text-[9px] font-extrabold text-gray-400">২</span>
                <div className="w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center font-bold text-[8px]">TR</div>
                <span className="font-bangla text-[9px] text-white">তাসনিম রহমান</span>
              </div>
              <span className="text-[8px] font-english text-emerald-400 font-bold">+১২৫ XP</span>
            </div>

            <div className="flex justify-between items-center p-1.5 px-2.5 bg-orange-500/10 border border-orange-500/20 rounded-xl">
              <div className="flex items-center gap-1.5 text-orange-400 font-semibold text-[9px]">
                <Flame className="w-3.5 h-3.5 fill-current" />
                <span className="font-bangla">তুমি ১৫ দিনের স্ট্রিকে আছো!</span>
              </div>
            </div>
          </div>
        </div>
      </FloatingElement>

      {/* Element 4: Translucent Geometric Sphere (Center-Right Background) */}
      <FloatingElement
        className="bottom-16 right-8 w-24 h-24 z-0"
        xRange={[6, -6]}
        yRange={[8, -8]}
        rRange={[15, -15]}
        duration={9}
        delay={4}
      >
        <div className="w-full h-full rounded-full bg-gradient-to-tr from-cyan-400/30 via-purple-500/10 to-transparent border border-white/20 shadow-2xl backdrop-blur-[2px] relative overflow-hidden flex items-center justify-center">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.2),transparent)]" />
          <div className="w-12 h-12 rounded-full border border-dashed border-white/10 animate-[spin_20s_linear_infinite]" />
        </div>
      </FloatingElement>
    </div>
  );
}
